(() => {
  if (window.LSLawnConditions) {
    window.LSLawnConditions.mountAll?.();
    return;
  }

  const PROFILE_KEY = 'ls-fit-finder-session-shared-v1';
  const LOCATION_KEY = 'ls-soil-location-v1';
  const REGION_STATES = {
    Florida: new Set(['FL']),
    'Transition Zone': new Set(['AR','DE','DC','KY','MD','MO','NC','OK','TN','VA','WV']),
    Southern: new Set(['AL','AZ','CA','GA','LA','MS','NV','NM','SC','TX']),
    Northern: new Set(['AK','CO','CT','ID','IL','IN','IA','KS','ME','MA','MI','MN','MT','NE','NH','NJ','NY','ND','OH','OR','PA','RI','SD','UT','VT','WA','WI','WY'])
  };
  const normalize = (value) => String(value || '').trim().toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, ' ').trim();
  const number = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const readStorage = (key) => {
    try {
      return JSON.parse(localStorage.getItem(key) || '{}');
    } catch (error) {
      return {};
    }
  };

  const readProfile = () => readStorage(PROFILE_KEY);
  const readLocation = () => readStorage(LOCATION_KEY);

  const readJson = (root, selector, fallback) => {
    try {
      return JSON.parse(root.querySelector(selector)?.textContent || '');
    } catch (error) {
      return fallback;
    }
  };

  const resolveRegion = (stateCode) => {
    const code = String(stateCode || '').trim().toUpperCase();
    if (!code) return '';
    return Object.entries(REGION_STATES).find(([, states]) => states.has(code))?.[0] || '';
  };

  const seasonMatches = (season, month) => {
    if (window.LSSoilTiming) return window.LSSoilTiming.seasonMatches(season, month);
    const value = normalize(season);
    if (value === 'spring') return month >= 1 && month <= 4;
    if (value === 'fall') return month >= 8 && month <= 11;
    if (value.includes('late spring') || value.includes('summer')) return month >= 5 && month <= 8;
    return true;
  };

  const appliesToLawn = (rule, grass) => {
    const selected = normalize(grass);
    return rule.lawnTypes.some((name) => normalize(name) === selected);
  };

  const appliesToRegion = (rule, region) => {
    const scope = normalize(rule.regionScope);
    if (!scope || scope === 'all' || scope === 'all regions' || scope === 'nationwide') return true;
    return scope === normalize(region);
  };

  const evaluateBeforeThreshold = (rule, average) => {
    const start = number(rule.recommendationStart);
    const target = number(rule.targetThreshold);
    if (start === null || target === null) return null;

    if (normalize(rule.trend) === 'warming') {
      if (average < start) return 'A Little Early';
      if (average < target - 5) return 'Good Timing';
      if (average < target) return 'Excellent Conditions';
      return 'Past Ideal Window';
    }

    if (normalize(rule.trend) === 'cooling') {
      if (average > start + 5) return 'A Little Early';
      if (average > start) return 'Approaching Window';
      if (average > target) return 'Excellent Conditions';
      return 'Past Ideal Window';
    }

    return null;
  };

  const evaluateWithinRange = (rule, average) => {
    const min = number(rule.idealMin);
    const max = number(rule.idealMax);
    const target = number(rule.targetThreshold);
    const season = normalize(rule.season);

    if (min === null || max === null) return null;
    if (average >= min && average <= max) return 'Excellent Conditions';

    if (average < min) {
      if (target !== null && target < min && average >= target) return 'Good Timing';
      if (season === 'fall') return 'Past Ideal Window';
      return 'A Little Early';
    }

    if (season === 'fall') return 'A Little Early';
    if (season.includes('spring') && !season.includes('summer')) return 'Past Ideal Window';
    return 'Good Timing';
  };

  const evaluateRule = (rule, average) => {
    if (window.LSSoilTiming) return window.LSSoilTiming.evaluateRule(rule, average);
    const activity = normalize(rule.activity);
    const season = normalize(rule.season);
    const target = number(rule.targetThreshold);

    if (activity === 'pre emergent' && season === 'fall' && target !== null) {
      return average >= target ? 'Excellent Conditions' : 'Past Ideal Window';
    }

    const direction = normalize(rule.direction);
    if (direction === 'apply before threshold') return evaluateBeforeThreshold(rule, average);
    if (direction === 'within range') return evaluateWithinRange(rule, average);
    return null;
  };

  const messageFor = (rule, status) => {
    if (status === 'A Little Early') return rule.beforeMessage || 'The timing window is approaching.';
    if (status === 'Approaching Window' || status === 'Good Timing') return rule.approachingMessage || 'Good timing for this lawn-care step.';
    if (status === 'Excellent Conditions') return rule.idealMessage || rule.approachingMessage || 'Conditions are in the ideal range.';
    if (status === 'Past Ideal Window') return rule.pastMessage || 'The ideal timing window has passed.';
    return 'Not recommended right now.';
  };

  const cardTitle = (rule) => {
    const activity = normalize(rule.activity);
    const season = normalize(rule.season);
    if (activity === 'pre emergent') return `${rule.season} Pre-Emergent`;
    if (activity === 'overseeding') return 'Cool-Season Seeding';
    if (activity === 'seeding' && season.includes('spring')) return 'Spring Seeding & Repair';
    if (activity === 'seeding') return 'Bermuda Seeding';
    return rule.name;
  };

  const matchingRules = (rules, grass, region, average, month) => {
    return rules
      .filter((rule) => rule.enabled !== false)
      .filter((rule) => normalize(rule.activity) !== 'winter overseeding')
      .filter((rule) => appliesToLawn(rule, grass))
      .filter((rule) => appliesToRegion(rule, region))
      .filter((rule) => seasonMatches(rule.season, month))
      .map((rule) => ({ ...rule, status: evaluateRule(rule, average) }))
      .filter((rule) => rule.status)
      .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
      .slice(0, 2);
  };

  const conflictFor = (rule, activeRules) => {
    if (!Array.isArray(rule.conflicts) || !rule.conflicts.length) return '';
    const activities = activeRules.map((item) => normalize(item.activity));
    const hasConflict = rule.conflicts.some((activity) => activities.includes(normalize(activity)));
    return hasConflict ? rule.conflictMessage || '' : '';
  };

  const regionMatchesProgram = (programRegion, region) => {
    const programValue = normalize(programRegion);
    const selectedRegion = normalize(region);
    if (!selectedRegion) return true;
    if (selectedRegion === 'florida') return programValue === 'florida' || programValue === 'southern';
    if (selectedRegion === 'southern') return programValue === 'southern' || programValue === 'florida';
    return programValue === selectedRegion;
  };

  const findProgramStep = (programs, grass, region, monthName) => {
    const selectedGrass = normalize(grass);
    const candidates = programs.filter((item) => item.lawnTypes.some((name) => normalize(name) === selectedGrass));
    const program = candidates.find((item) => regionMatchesProgram(item.region, region)) || candidates[0];
    if (!program) return null;

    const step = program.steps.find((item) => item.activeMonths.some((month) => normalize(month) === normalize(monthName)));
    return step ? { ...step, programName: program.name, programRegion: program.region } : null;
  };

  const programOverlapsRule = (step, rule) => {
    if (!step || !rule) return false;

    const programText = normalize([step.title, step.name, step.goal, step.description].filter(Boolean).join(' '));
    const activity = normalize(rule.activity);

    if (activity === 'pre emergent') return programText.includes('pre emergent');
    if (activity === 'seeding' || activity === 'overseeding') return programText.includes('seed');

    return false;
  };

  const createTimingCard = (rule, allActiveRules) => {
    const article = document.createElement('article');
    article.className = 'ls-lawn-conditions__card';

    const status = document.createElement('span');
    status.className = 'ls-lawn-conditions__status';
    status.textContent = rule.status;

    const heading = document.createElement('h3');
    heading.textContent = cardTitle(rule);

    const copy = document.createElement('p');
    copy.textContent = messageFor(rule, rule.status);

    article.append(status, heading, copy);

    const conflict = conflictFor(rule, allActiveRules);
    if (conflict) {
      const note = document.createElement('p');
      note.className = 'ls-lawn-conditions__conflict';
      note.textContent = conflict;
      article.append(note);
    }

    return article;
  };

  const createProgramCard = (step, label, buttonLabel) => {
    const article = document.createElement('article');
    article.className = 'ls-lawn-conditions__card ls-lawn-conditions__card--program';

    const eyebrow = document.createElement('span');
    eyebrow.className = 'ls-lawn-conditions__program-label';
    eyebrow.textContent = label;

    const heading = document.createElement('h3');
    heading.textContent = step.title || step.name;

    const copy = document.createElement('p');
    copy.textContent = step.applicationTiming || step.description || '';

    article.append(eyebrow, heading, copy);

    if (step.product?.url) {
      const link = document.createElement('a');
      link.className = 'ls-lawn-conditions__link';
      link.href = step.product.url;
      link.textContent = buttonLabel;
      article.append(link);
    }

    return article;
  };

  const createCombinedCard = (rule, step, allActiveRules, buttonLabel) => {
    const article = createTimingCard(rule, allActiveRules);
    article.classList.add('ls-lawn-conditions__card--program');

    const heading = article.querySelector('h3');
    if (heading) heading.textContent = step.title || step.name || cardTitle(rule);

    if (step.product?.url) {
      const link = document.createElement('a');
      link.className = 'ls-lawn-conditions__link';
      link.href = step.product.url;
      link.textContent = buttonLabel;
      article.append(link);
    }

    return article;
  };

  const render = (root) => {
    const cards = root.querySelector('[data-lawn-condition-cards]');
    const empty = root.querySelector('[data-lawn-condition-empty]');
    if (!cards || !empty) return;

    const rules = readJson(root, '[data-lawn-condition-rules]', []);
    const programs = readJson(root, '[data-lawn-condition-programs]', []);
    const profile = readProfile();
    const location = readLocation();
    const soil = window.LSSoilData;
    const grass = profile.grass || '';
    const stateCode = profile.state || location.stateCode || '';
    const region = resolveRegion(stateCode);

    cards.replaceChildren();

    if (!soil?.average) {
      empty.hidden = false;
      empty.textContent = root.dataset.locationPrompt || 'Enter your ZIP code above to see local recommendations.';
      return;
    }

    if (!grass) {
      empty.hidden = false;
      empty.textContent = root.dataset.lawnPrompt || 'Choose your lawn type above to see personalized recommendations.';
      return;
    }

    empty.hidden = true;
    const now = new Date();
    const month = now.getMonth() + 1;
    const monthName = now.toLocaleString('en-US', { month: 'long' });
    const average = Number(soil.average);
    const activeRules = root.dataset.showTiming === 'true' ? matchingRules(rules, grass, region, average, month) : [];
    const step = root.dataset.showProgram === 'true' ? findProgramStep(programs, grass, region, monthName) : null;
    const overlappingRule = step ? activeRules.find((rule) => programOverlapsRule(step, rule)) : null;

    activeRules.forEach((rule) => {
      if (rule === overlappingRule) {
        cards.append(createCombinedCard(rule, step, activeRules, root.dataset.programButton || 'View Recommended Product'));
        return;
      }

      cards.append(createTimingCard(rule, activeRules));
    });

    if (step && !overlappingRule) {
      cards.append(createProgramCard(step, root.dataset.programLabel || 'This Month', root.dataset.programButton || 'View Recommended Product'));
    }

    if (!cards.children.length) {
      empty.hidden = false;
      empty.textContent = 'No seasonal lawn-care action is recommended right now.';
    }
  };

  const mount = (root) => {
    if (!root || root.dataset.lawnConditionsReady === 'true') return;
    root.dataset.lawnConditionsReady = 'true';
    render(root);
  };

  const mountAll = () => document.querySelectorAll('[data-lawn-conditions]').forEach(mount);
  const renderAll = () => document.querySelectorAll('[data-lawn-conditions]').forEach(render);

  window.addEventListener('ls:soil-data', renderAll);
  window.addEventListener('ls:lawn-profile', renderAll);
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.matches?.('[data-lawn-conditions]') ? event.target : event.target.querySelector?.('[data-lawn-conditions]');
    if (root) mount(root);
  });

  window.LSLawnConditions = { mountAll, renderAll, evaluateRule, resolveRegion };
  mountAll();
})();
